const SPEECH_HIGHLIGHT_NAME = "dino-reading-word";
const SPEECH_HIGHLIGHT_STYLE_ID = "dino-reading-word-style";
const SPEECH_EXCLUDED_SELECTOR = [
    "script",
    "style",
    "svg",
    "button",
    "input",
    "textarea",
    "select",
    "[hidden]",
    "[aria-hidden='true']",
    "[data-speech-ignore]"
].join(",");
const SPEECH_BLOCK_SELECTOR = [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "li",
    "tr",
    "blockquote",
    "dt",
    "dd",
    "figcaption"
].join(",");
const SPEECH_TOKEN_CONTENT = /[\p{L}\p{N}]/u;

interface SpeechTextToken {
    start: number;
    end: number;
    word: string;
    range: Range;
}

interface SpeechTextHighlighter {
    text: string;
    segmentBreaks: readonly number[];
    activate(charIndex: number): string | null;
    clear(): void;
}

interface HighlightRegistryLike {
    set(name: string, highlight: unknown): void;
    delete(name: string): boolean;
}

type HighlightConstructor = new (...ranges: Range[]) => unknown;

function getHighlightApi(): {
    registry: HighlightRegistryLike;
    Highlight: HighlightConstructor;
} | null {
    if (typeof window === "undefined" || typeof CSS === "undefined") {
        return null;
    }

    const registry = (
        CSS as typeof CSS & { highlights?: HighlightRegistryLike }
    ).highlights;
    const Highlight = (
        window as Window & { Highlight?: HighlightConstructor }
    ).Highlight;

    return registry && Highlight
        ? { registry, Highlight }
        : null;
}

function ensureSpeechHighlightStyle(documentRef: Document): void {
    if (documentRef.getElementById(SPEECH_HIGHLIGHT_STYLE_ID)) return;

    const style = documentRef.createElement("style");
    style.id = SPEECH_HIGHLIGHT_STYLE_ID;
    style.textContent = `::highlight(${SPEECH_HIGHLIGHT_NAME}) {
        background: #ffdc68;
        color: #03271f;
        text-decoration: underline;
        text-decoration-color: #0b7f5f;
        text-decoration-thickness: 0.12em;
        text-underline-offset: 0.16em;
    }`;
    documentRef.head.append(style);
}

function isReadableTextNode(node: Text): boolean {
    const parent = node.parentElement;
    if (!parent || !node.data.trim()) return false;

    return !parent.closest(SPEECH_EXCLUDED_SELECTOR);
}

function getSpeechBlock(
    node: Text,
    root: HTMLElement
): Element {
    const block =
        node.parentElement?.closest(
            SPEECH_BLOCK_SELECTOR
        );

    return block && root.contains(block)
        ? block
        : root;
}

function scrollRangeIntoView(range: Range): void {
    const rect = range.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isOutsideViewport = rect.bottom < 72 || rect.top > viewportHeight - 72;

    if (isOutsideViewport) {
        range.startContainer.parentElement?.scrollIntoView({
            behavior: "smooth",
            block: "center"
        });
    }
}

function createSpeechTextHighlighter(element: HTMLElement): SpeechTextHighlighter {
    const documentRef = element.ownerDocument;
    const nodeFilter = documentRef.defaultView?.NodeFilter ?? NodeFilter;
    const walker = documentRef.createTreeWalker(
        element,
        nodeFilter.SHOW_TEXT,
        {
            acceptNode: node => (
                node instanceof Text && isReadableTextNode(node)
                    ? nodeFilter.FILTER_ACCEPT
                    : nodeFilter.FILTER_REJECT
            )
        }
    );
    const tokens: SpeechTextToken[] = [];
    const words: string[] = [];
    const segmentBreaks: number[] = [];
    let speechOffset = 0;
    let previousBlock: Element | null = null;
    let current = walker.nextNode();

    while (current) {
        const textNode = current as Text;
        const block =
            getSpeechBlock(
                textNode,
                element
            );
        const matches = textNode.data.matchAll(/\S+/gu);

        for (const match of matches) {
            const word = match[0];
            if (!SPEECH_TOKEN_CONTENT.test(word)) continue;

            const localStart = match.index;
            const range = documentRef.createRange();
            range.setStart(textNode, localStart);
            range.setEnd(textNode, localStart + word.length);

            if (words.length > 0) {
                speechOffset += 1;

                if (block !== previousBlock) {
                    segmentBreaks.push(
                        speechOffset
                    );
                }
            }

            tokens.push({
                start: speechOffset,
                end: speechOffset + word.length,
                word,
                range
            });
            words.push(word);
            speechOffset += word.length;
            previousBlock = block;
        }

        current = walker.nextNode();
    }

    const highlightApi = getHighlightApi();
    if (highlightApi) ensureSpeechHighlightStyle(documentRef);
    let activeToken: SpeechTextToken | null = null;

    return {
        text: words.join(" "),
        segmentBreaks,
        activate(charIndex: number): string | null {
            const token = tokens.find(candidate => (
                charIndex >= candidate.start && charIndex < candidate.end
            )) ?? tokens.find(candidate => candidate.start >= charIndex) ?? null;

            if (!token) return null;
            if (token === activeToken) return token.word;
            activeToken = token;

            if (highlightApi) {
                highlightApi.registry.set(
                    SPEECH_HIGHLIGHT_NAME,
                    new highlightApi.Highlight(token.range)
                );
                scrollRangeIntoView(token.range);
            }

            return token.word;
        },
        clear(): void {
            activeToken = null;
            highlightApi?.registry.delete(SPEECH_HIGHLIGHT_NAME);
        }
    };
}

function createSpeechRangeHighlighter(
    range: Range,
    word: string
): SpeechTextHighlighter {
    const documentRef = range.startContainer.ownerDocument;
    if (!documentRef) {
        throw new Error("The selected word is not attached to a document.");
    }
    const highlightApi = getHighlightApi();
    if (highlightApi) ensureSpeechHighlightStyle(documentRef);
    let active = false;

    return {
        text: word,
        segmentBreaks: [],
        activate(): string {
            if (!active && highlightApi) {
                highlightApi.registry.set(
                    SPEECH_HIGHLIGHT_NAME,
                    new highlightApi.Highlight(range)
                );
                scrollRangeIntoView(range);
            }
            active = true;
            return word;
        },
        clear(): void {
            active = false;
            highlightApi?.registry.delete(SPEECH_HIGHLIGHT_NAME);
        }
    };
}

function findSpeechWord(text: string, charIndex: number): string | null {
    for (const match of text.matchAll(/\S+/gu)) {
        const start = match.index;
        const end = start + match[0].length;
        if (charIndex >= start && charIndex < end) return match[0];
        if (start >= charIndex) return match[0];
    }

    return null;
}

export {
    SPEECH_HIGHLIGHT_NAME,
    createSpeechRangeHighlighter,
    createSpeechTextHighlighter,
    findSpeechWord
};
export type { SpeechTextHighlighter };
